import { Autocomplete, Box, Chip, FormLabel, TextField, Typography } from "@mui/material"
import type { SxProps, TextFieldProps, Theme } from "@mui/material"
import { useUsers } from "@crm-src/hooks/useUsers"
import type { User } from "@crm-src/hooks/useUsers"

interface MultiUserSelectorProps {
  value?: string[] // Array of user IDs
  onValueChange: (value: string[]) => void
  label?: string
  placeholder?: string
  required?: boolean
  className?: string
  /** Fall back to the assignable roster when the admin user list is refused —
   * see `useUsers({ assignableFallback })`. */
  assignableFallback?: boolean
}

export function MultiUserSelector({
  value = [],
  onValueChange,
  label = "Assigned Salespeople",
  placeholder = "Select salespeople...",
  required = false,
  className,
  assignableFallback = false,
}: MultiUserSelectorProps) {
  const { users, loading, error } = useUsers({ assignableFallback })

  // Ensure all values are strings for consistent comparison
  const normalizedValue = value.map((id) => String(id))

  const selectedUsers = users.filter((user) => normalizedValue.includes(String(user.id)))

  const rootSx: SxProps<Theme> = { display: "flex", flexDirection: "column", gap: 1 }

  return (
    <Box sx={rootSx} {...(className ? { className } : {})}>
      {label && (
        <FormLabel sx={{ color: "text.primary", fontSize: 14, fontWeight: 500 }}>
          {label}
          {required && (
            <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
              *
            </Box>
          )}
        </FormLabel>
      )}

      <Autocomplete
        multiple
        disableCloseOnSelect
        options={users}
        value={selectedUsers}
        loading={loading}
        disabled={loading}
        getOptionLabel={(user) => user.full_name}
        isOptionEqualToValue={(option, val) => String(option.id) === String(val.id)}
        noOptionsText={error ? "Error loading users" : "No users found."}
        loadingText="Loading users..."
        onChange={(_event, newSelected) => {
          onValueChange((newSelected as User[]).map((user) => String(user.id)))
        }}
        renderOption={(props, user) => (
          <Box component="li" {...props} key={user.id}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "success.main",
                mr: 1,
                flexShrink: 0,
              }}
            />
            <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, color: "text.primary" }}>{user.full_name}</Typography>
              {user.email && (
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{user.email}</Typography>
              )}
            </Box>
          </Box>
        )}
        renderTags={(selected, getTagProps) =>
          selected.map((user, index) => {
            const { key, ...tagProps } = getTagProps({ index })
            return (
              <Chip
                {...tagProps}
                key={key}
                size="small"
                label={user.full_name}
                icon={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "success.main",
                    }}
                  />
                }
              />
            )
          })
        }
        renderInput={(params) => {
          // AutocompleteRenderInputParams doesn't line up with TextFieldProps
          // under exactOptionalPropertyTypes (InputLabelProps shape + size).
          // Narrow it (valid at runtime) — same workaround as ui/multi-select.
          const inputProps = params as TextFieldProps
          return (
            <TextField
              {...inputProps}
              size="small"
              placeholder={selectedUsers.length === 0 ? placeholder : ""}
            />
          )
        }}
      />
    </Box>
  )
}
